import { useState, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, ChevronRight, FileText, List } from 'lucide-react';
import type { SourceInfo } from '../../types';
import { useChatStore } from '../../store/chatStore';
import { MermaidDiagram } from './MermaidDiagram';

interface MarkdownRendererProps {
  content: string;
  sources?: SourceInfo[];
  showTableOfContents?: boolean;
  onBreadcrumbClick?: (path: string[]) => void;
  /** When set, ```sql blocks get a "Run in SQL Investigation" action. */
  onRunSql?: (sql: string) => void;
}

// Extract headings for table of contents
function extractHeadings(content: string): { level: number; text: string; id: string }[] {
  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  const headings: { level: number; text: string; id: string }[] = [];
  let match;

  while ((match = headingRegex.exec(content)) !== null) {
    const level = match[1].length;
    const text = match[2].trim();
    const id = text.toLowerCase().replace(/[^\w]+/g, '-');
    headings.push({ level, text, id });
  }

  return headings;
}

/**
 * Renders markdown content with GFM support, syntax-highlighted code blocks,
 * Mermaid diagrams, inline citation chips, breadcrumb navigation, and a table of contents.
 */
export function MarkdownRenderer({
  content,
  sources,
  showTableOfContents = true,
  onBreadcrumbClick,
  onRunSql,
}: MarkdownRendererProps) {
  const [showToc, setShowToc] = useState(false);
  const { setSelectedSource } = useChatStore();

  // Extract headings for ToC
  const headings = useMemo(() => extractHeadings(content), [content]);
  const hasHeadings = headings.length > 2;

  // Process content to convert source references and breadcrumbs
  const processedContent = useMemo(() => {
    let processed = content;

    // Strip any leftover [1], [2] citation markers the LLM may produce
    processed = processed.replace(/\s*\[(\d+)\]\s*/g, ' ');

    // Fix wall-of-text steps: "Step 1: ... Step 2: ... Step 3: ..." all
    // on one line → split into a proper numbered markdown list.
    if (/Step\s+\d+\s*[:.]/i.test(processed) && /Step\s+[2-9]\d*\s*[:.]/i.test(processed)) {
      processed = processed.replace(
        /\s*Step\s+(\d+)\s*[:.]\s*/gi,
        (_match, num) => `\n${num}. `,
      ).trim();
    }

    // Strip figure references like [Figure 33 –DG Gate module search] since
    // images cannot be displayed in the chat interface.
    processed = processed.replace(/\s*\[Figure\s+\d+[^\]]*\]\s*/gi, ' ').trim();

    // Convert breadcrumb patterns [[Path > To > Item]] to clickable breadcrumbs
    processed = processed.replace(
      /\[\[([^\]]+)\]\]/g,
      (_, path) => `<breadcrumb>${path}</breadcrumb>`
    );

    return processed;
  }, [content, sources]);

  const scrollToHeading = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setShowToc(false);
  }, []);

  return (
    // text-gray-900: rendered content must not inherit a muted ancestor color —
    // analysis text was unreadably light for some users (WCAG AAA body text).
    // NOTE: do NOT add the global `markdown-content` class here — index.css's
    // blockquote/heading/table rules under it restyle every consumer at once
    // and re-break the contrast fix above (audited 2026-08-30).
    <div className="relative text-gray-900">
      {/* Table of Contents toggle */}
      {showTableOfContents && hasHeadings && (
        <div className="mb-3">
          <button
            onClick={() => setShowToc(!showToc)}
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            <List size={14} />
            {showToc ? 'Hide' : 'Show'} Table of Contents
          </button>

          {/* ToC Panel */}
          {showToc && (
            <nav className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <ul className="space-y-1">
                {headings.map((heading, index) => (
                  <li
                    key={index}
                    style={{ paddingLeft: `${(heading.level - 1) * 0.75}rem` }}
                  >
                    <button
                      onClick={() => scrollToHeading(heading.id)}
                      className="text-sm text-gray-600 hover:text-primary-600 transition-colors text-left"
                    >
                      {heading.text}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </div>
      )}

      {/* Main content */}
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Code block with copy button
          code({ className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            const codeContent = String(children).replace(/\n$/, '');

            // No language tag: multi-line content is still a code BLOCK
            // (SQL/Java pasted without a fence language) — mono, small,
            // scrollable. Single-line stays inline.
            if (!match) {
              if (codeContent.includes('\n')) {
                return (
                  <pre className="my-2 p-3 bg-gray-900 text-gray-100 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed">
                    {codeContent}
                  </pre>
                );
              }
              return (
                <code
                  className="px-1.5 py-0.5 bg-gray-100 rounded text-sm font-mono text-gray-800"
                  {...props}
                >
                  {children}
                </code>
              );
            }

            // Mermaid diagrams — render as interactive SVG
            if (match[1] === 'mermaid') {
              return <MermaidDiagram chart={codeContent} />;
            }

            // Code block with syntax highlighting and copy button
            return (
              <CodeBlockWithCopy
                language={match[1]}
                code={codeContent}
                onRunSql={onRunSql}
              />
            );
          },

          // Headings with IDs for ToC linking
          h1: ({ children }) => {
            const id = String(children).toLowerCase().replace(/[^\w]+/g, '-');
            return <h1 id={id} className="scroll-mt-4">{children}</h1>;
          },
          h2: ({ children }) => {
            const id = String(children).toLowerCase().replace(/[^\w]+/g, '-');
            return <h2 id={id} className="scroll-mt-4">{children}</h2>;
          },
          h3: ({ children }) => {
            const id = String(children).toLowerCase().replace(/[^\w]+/g, '-');
            return <h3 id={id} className="scroll-mt-4">{children}</h3>;
          },

          hr: () => <hr className="my-4 border-t border-gray-300" />,

          // GFM tables (Evidence sections etc.): small sans type with real
          // borders, scrolling in their own container — regardless of the
          // prose font around them.
          table: ({ children }) => (
            <div className="overflow-x-auto my-3">
              <table className="text-xs font-sans border-collapse">{children}</table>
            </div>
          ),

          // Custom link styling
          a({ href, children }) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                {children}
              </a>
            );
          },

          // Handle citation and breadcrumb placeholders in all block elements
          // Use <div> instead of <p> to avoid invalid DOM nesting when
          // CitationChipWithPreview renders block-level elements inside.
          p({ children }) {
            return <div className="mb-1.5 leading-snug">{processChildren(children, sources, setSelectedSource, onBreadcrumbClick)}</div>;
          },
          li({ children }) {
            return <li>{processChildren(children, sources, setSelectedSource, onBreadcrumbClick)}</li>;
          },
          td({ children }) {
            return (
              <td className="border border-gray-200 px-2.5 py-1.5 align-top text-gray-800">
                {processChildren(children, sources, setSelectedSource, onBreadcrumbClick)}
              </td>
            );
          },
          th({ children }) {
            return (
              <th className="border border-gray-300 bg-gray-50 px-2.5 py-1.5 text-left font-semibold text-gray-700 whitespace-nowrap">
                {processChildren(children, sources, setSelectedSource, onBreadcrumbClick)}
              </th>
            );
          },
          blockquote({ children }) {
            return <blockquote>{processChildren(children, sources, setSelectedSource, onBreadcrumbClick)}</blockquote>;
          },
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}

// Code block with copy to clipboard
function CodeBlockWithCopy({
  language,
  code,
  onRunSql,
}: {
  language: string;
  code: string;
  onRunSql?: (sql: string) => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = () => {
    const ext = getFileExtension(language);
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `config.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="relative group my-3">
      {/* Language badge and actions */}
      <div className="absolute top-0 right-0 flex items-center gap-1 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="text-xs text-gray-400 mr-2">{language}</span>
        {onRunSql && language === 'sql' && (
          <button
            onClick={() => onRunSql(code)}
            className="px-2 py-1 rounded bg-primary-600 hover:bg-primary-500 text-white text-xs font-medium transition-colors"
            title="Open SQL Investigation with this query"
          >
            Run in SQL Investigation
          </button>
        )}
        <button
          onClick={handleDownload}
          className="p-1.5 rounded bg-gray-700/50 hover:bg-gray-700 text-gray-300 transition-colors"
          title="Download"
        >
          <FileText size={14} />
        </button>
        <button
          onClick={handleCopy}
          className="p-1.5 rounded bg-gray-700/50 hover:bg-gray-700 text-gray-300 transition-colors"
          title={copied ? 'Copied!' : 'Copy to clipboard'}
        >
          {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
        </button>
      </div>

      <SyntaxHighlighter
        style={oneLight}
        language={language}
        PreTag="div"
        customStyle={{
          margin: 0,
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          paddingTop: '2rem',
        }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

// Get file extension for download
function getFileExtension(language: string): string {
  const extensions: Record<string, string> = {
    javascript: 'js',
    typescript: 'ts',
    python: 'py',
    json: 'json',
    yaml: 'yaml',
    yml: 'yml',
    bash: 'sh',
    shell: 'sh',
    sql: 'sql',
    xml: 'xml',
    html: 'html',
    css: 'css',
  };
  return extensions[language] || 'txt';
}

// Helper to process citation and breadcrumb markers
function processChildren(
  children: React.ReactNode,
  sources?: SourceInfo[],
  onSourceClick?: (source: SourceInfo) => void,
  onBreadcrumbClick?: (path: string[]) => void
): React.ReactNode {
  if (typeof children === 'string') {
    // Split by citation and breadcrumb markers
    const parts = children.split(/(<cite-\d+>|<breadcrumb>[^<]+<\/breadcrumb>)/g);

    return parts.map((part, index) => {
      // Handle citations
      const citeMatch = part.match(/<cite-(\d+)>/);
      if (citeMatch && sources) {
        const sourceNum = parseInt(citeMatch[1], 10);
        const source = sources[sourceNum - 1];
        if (source) {
          return (
            <CitationChipWithPreview
              key={index}
              number={sourceNum}
              source={source}
              onClick={() => onSourceClick?.(source)}
            />
          );
        }
      }

      // Handle breadcrumbs
      const breadcrumbMatch = part.match(/<breadcrumb>([^<]+)<\/breadcrumb>/);
      if (breadcrumbMatch) {
        const pathString = breadcrumbMatch[1];
        const pathParts = pathString.split('>').map((p) => p.trim());
        return (
          <ActionableBreadcrumb
            key={index}
            path={pathParts}
            onClick={() => onBreadcrumbClick?.(pathParts)}
          />
        );
      }

      return part;
    });
  }

  if (Array.isArray(children)) {
    return children.map((child, index) => {
      if (typeof child === 'string') {
        return (
          <span key={index}>
            {processChildren(child, sources, onSourceClick, onBreadcrumbClick)}
          </span>
        );
      }
      return child;
    });
  }

  return children;
}

// Citation chip with hover preview
function CitationChipWithPreview({
  number,
  source,
  onClick,
}: {
  number: number;
  source: SourceInfo;
  onClick?: () => void;
}) {
  const [showPreview, setShowPreview] = useState(false);

  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => setShowPreview(true)}
      onMouseLeave={() => setShowPreview(false)}
    >
      <button
        onClick={onClick}
        className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 mx-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition-colors cursor-pointer"
      >
        {number}
      </button>

      {/* Hover preview */}
      {showPreview && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 z-50 animate-fade-in">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3">
            <div className="flex items-start gap-2 mb-2">
              <FileText size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <span className="block text-sm font-medium text-gray-800 truncate">
                  {source.title}
                </span>
                {source.section && (
                  <span className="block text-xs text-gray-500 truncate">{source.section}</span>
                )}
              </div>
            </div>

            {source.snippet && (
              <span className="block text-xs text-gray-600 line-clamp-3 mb-2">
                {source.snippet}
              </span>
            )}

            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400">
                Relevance: {Math.round(source.relevance_score * 100)}%
              </span>
              <span className="text-primary-600">Click to view</span>
            </div>

            {/* Arrow */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
              <div className="w-2 h-2 bg-white border-r border-b border-gray-200 transform rotate-45" />
            </div>
          </div>
        </div>
      )}
    </span>
  );
}

// Actionable breadcrumb component
function ActionableBreadcrumb({
  path,
  onClick,
}: {
  path: string[];
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 px-2 py-1 mx-1 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md text-sm text-blue-700 transition-colors"
      title={`Navigate to: ${path.join(' > ')}`}
    >
      {path.map((part, index) => (
        <span key={index} className="flex items-center">
          {index > 0 && <ChevronRight size={12} className="mx-0.5 text-blue-400" />}
          <span className={index === path.length - 1 ? 'font-medium' : ''}>
            {part}
          </span>
        </span>
      ))}
    </button>
  );
}
